import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { creatorHandle } from "@/components/tableKit"
import { type TalentBrowseProps, type TalentRowState, type TalentSelection } from "@/components/talentBrowse"
import { toast } from "@/components/ui/toast"
import { getBrowseFilterFromEligibility } from "@/utils/browseFilter"
import { analyticsService } from "../../services/analytics"
import { useExchangeRates } from "../../services/exchangeRates"
import {
    UserContext,
    getClientById,
    getUserById,
    submitPitchesBatch,
    useCampaignBookings,
    useCampaignById,
    useFreshCities,
    useFreshCountries,
    useFreshLists,
    useFreshTalent,
    usePitchesByCampaignId,
    warmFullTalent,
} from "../../services/firebase"
import { contentTags } from "../../shared/grailConstants"
import { asPlatforms } from "../../shared/platforms"
import { tiptapJsonToPlainText } from "../../shared/tiptapText"
import type { CampaignType, Client, Currency, List, Pitch, Talent } from "../../shared/types"
import { buildListSwitcherOptions } from "../../utils/lists"
import {
    isPitchSubmittable,
    pitchPriceWithinCampaignBounds,
    talentMatchesPitchingEligibility,
} from "../../utils/pitching/eligibility"
import { canPitchCreators } from "../../utils/pitching/permissions"
import type { PitchCartItem } from "./components/PitchCart"

type CartEntry = {
    price: number
    notes?: Record<string, unknown>
}

/**
 * Load campaign, talent browse, cart, and batch submit for Add Pitch.
 */
export function usePitching() {
    const { campaignId } = useParams<{ campaignId: string }>()
    const navigate = useNavigate()
    const user = useContext(UserContext)
    const queryClient = useQueryClient()
    const exchangeRates = useExchangeRates()
    const [browseListId, setBrowseListId] = useState("my-roster")
    // Browse roster first. Attach the full catalog only after the switcher leaves My Roster.
    const isFullList = browseListId !== "my-roster"
    // Latch: going back to My Roster must not drop All-list picks from the cart / submit (they resolve against rawTalent).
    const [hasSelectedFullList, setHasSelectedFullList] = useState(isFullList)
    if (isFullList && !hasSelectedFullList) {
        setHasSelectedFullList(true)
    }

    const { campaign, loading: campaignLoading, error: campaignError } = useCampaignById(campaignId)
    const [client, setClient] = useState<Client>()
    const [cart, setCart] = useState<Record<string, CartEntry>>({})
    const [priceDrafts, setPriceDrafts] = useState<Record<string, number>>({})
    const [submitting, setSubmitting] = useState(false)
    const [blockedTalentIds, setBlockedTalentIds] = useState<Set<string>>(new Set())

    const { pitches, loading: pitchesLoading } = usePitchesByCampaignId(campaignId)
    const { bookings, loading: bookingsLoading } = useCampaignBookings(campaignId)

    const countriesQuery = useFreshCountries()
    const countryList = countriesQuery.flatData.filter((country) => country.liveOnAirtable).map((country) => country.label).sort()
    const cityQuery = useFreshCities()
    const cityList = cityQuery.flatData.map((cityDoc) => cityDoc.city).sort()
    const tagList = contentTags

    const systemListsQuery = useFreshLists({})

    const partialTalentQuery = useFreshTalent({
        enabled: Boolean(user?.roles?.manager && user.id),
        managerId: user?.id,
        ...(user?.roles?.superAdmin && { superAdmin: true }),
    })
    const fullTalentQuery = useFreshTalent({
        enabled: isFullList || hasSelectedFullList,
        ...(user?.roles?.superAdmin && { superAdmin: true }),
    })
    const partialTalent = partialTalentQuery.flatData
    const rawTalent = (isFullList || hasSelectedFullList) && fullTalentQuery.flatData.length
        ? fullTalentQuery.flatData
        : partialTalent

    // Fetch the catalog into RQ/IDB without mounting the hook, so All can attach from cache. Does not price/filter.
    useEffect(() => {
        if (!user?.id) return
        void warmFullTalent(queryClient)
    }, [user?.id, queryClient])

    const platforms = asPlatforms(campaign?.campaignPlatform)
    const campaignType: CampaignType = campaign?.campaignType || "Brand"
    const currency: Currency = campaign?.currency || "USD"
    const showNotesAndRates = campaign?.showNotesAndRates !== false

    useEffect(() => {
        if (!campaignError) {
            return
        }
        console.error(campaignError)
        analyticsService().logError(campaignError)
        toast.add({
            type: "error",
            title: "Couldn’t load this campaign",
            description: "This campaign may have been deleted. Refresh and try again.",
        })
    }, [campaignError])

    useEffect(() => {
        const bookerUid = campaign?.booker?.uid
        setClient(undefined)
        if (!bookerUid) {
            return
        }
        let cancelled = false
        async function loadClient() {
            try {
                const booker = await getUserById(bookerUid!)
                if (cancelled || !booker.clientId) {
                    return
                }
                const nextClient = await getClientById(booker.clientId)
                if (!cancelled) {
                    setClient(nextClient)
                }
            } catch (error) {
                console.error(error)
            }
        }
        void loadClient()
        return () => {
            cancelled = true
        }
    }, [campaign?.booker?.uid])

    const userId = user?.id
    const lists: List[] = useMemo(
        () =>
            buildListSwitcherOptions({
                userId,
                rawTalent,
                systemLists: systemListsQuery.flatData,
                campaignType,
            }),
        [userId, rawTalent, systemListsQuery.flatData, campaignType],
    )

    const selected: TalentSelection = useMemo(() => {
        const record: TalentSelection = {}
        for (const [id, entry] of Object.entries(cart)) {
            record[id] = { price: entry.price }
        }
        return record
    }, [cart])

    const pitchByTalentId = useMemo(() => {
        const map = new Map<string, Pitch>()
        for (const pitch of pitches) {
            map.set(pitch.talentId, pitch)
        }
        return map
    }, [pitches])

    const bookedWithoutPitch = useMemo(() => {
        const ids = new Set<string>()
        for (const booking of bookings) {
            if (!booking.talentId || booking.pitchId) {
                continue
            }
            if (!pitchByTalentId.has(booking.talentId)) {
                ids.add(booking.talentId)
            }
        }
        return ids
    }, [bookings, pitchByTalentId])

    const getPitchRowState = useCallback((tal: Talent): TalentRowState => {
        const existingPitch = pitchByTalentId.get(tal.id)
        const blocked = blockedTalentIds.has(tal.id)
        const booked = bookedWithoutPitch.has(tal.id)
        const eligible = talentMatchesPitchingEligibility(tal, campaign?.eligibility, platforms)
        let status: NonNullable<TalentRowState["status"]> = "available"
        if (existingPitch || blocked || booked) {
            status = existingPitch?.status || "pitched"
        } else if (!eligible) {
            status = "ineligible"
        }
        const selectable = status === "available"
        const draftPrice = priceDrafts[tal.id]
        const price = selected[tal.id]?.price ?? draftPrice
        return {
            status,
            pitcherName: showNotesAndRates && existingPitch?.managerEmail ? existingPitch.managerEmail : undefined,
            selectable,
            ...(draftPrice != null ? { price: draftPrice } : {}),
            priceInvalid:
                selectable &&
                price != null &&
                !pitchPriceWithinCampaignBounds(price, campaign?.minPricePerCreator, campaign?.maxPricePerCreator),
        }
    }, [pitchByTalentId, blockedTalentIds, bookedWithoutPitch, campaign?.eligibility, campaign?.minPricePerCreator, campaign?.maxPricePerCreator, platforms, selected, priceDrafts, showNotesAndRates])

    const cartItems: PitchCartItem[] = useMemo(() => {
        return Object.entries(cart).flatMap(([talentId, entry]) => {
            const talent = rawTalent.find((tal) => tal.id === talentId)
            if (!talent) {
                return []
            }
            return [{ talent, price: entry.price, notes: entry.notes }]
        })
    }, [cart, rawTalent])

    const minPrice = campaign?.minPricePerCreator
    const maxPrice = campaign?.maxPricePerCreator

    const handleSelectionChange = useCallback((next: TalentSelection) => {
        setCart((prev) => {
            const merged: Record<string, CartEntry> = {}
            for (const [id, entry] of Object.entries(next)) {
                if (!isCartablePrice(entry.price, minPrice, maxPrice)) {
                    continue
                }
                merged[id] = { price: entry.price, notes: prev[id]?.notes }
            }
            return merged
        })
        setPriceDrafts(() => {
            const drafts: Record<string, number> = {}
            for (const [id, entry] of Object.entries(next)) {
                if (!isCartablePrice(entry.price, minPrice, maxPrice) && entry.price > 0) {
                    drafts[id] = entry.price
                }
            }
            return drafts
        })
    }, [minPrice, maxPrice])

    const allowed = Boolean(campaign && canPitchCreators(user, campaign))
    const talentLoading =
        (!rawTalent.length &&
            (partialTalentQuery.isFetching ||
                partialTalentQuery.isLoading ||
                ((isFullList || hasSelectedFullList) && (fullTalentQuery.isFetching || fullTalentQuery.isLoading)))) ||
        pitchesLoading ||
        bookingsLoading

    const defaultFilters = campaign ? getBrowseFilterFromEligibility(campaign.eligibility) : undefined

    const browse: TalentBrowseProps = {
        talent: rawTalent,
        lists,
        platforms,
        campaignType,
        currency,
        exchangeRates,
        selected,
        onSelectionChange: handleSelectionChange,
        defaultListId: "my-roster",
        onListIdChange: setBrowseListId,
        defaultFilters,
        filterSheet: {
            showPriceChips: true,
            currency,
            countryList,
            tagList,
            cityList,
        },
        rowState: getPitchRowState,
    }

    function onRemove(talentId: string) {
        setCart((prev) => omitKey(prev, talentId))
        setPriceDrafts((prev) => omitKey(prev, talentId))
    }

    function onNotesChange(talentId: string, notes: Record<string, unknown>) {
        setCart((prev) => {
            if (!prev[talentId]) {
                return prev
            }
            return { ...prev, [talentId]: { ...prev[talentId], notes } }
        })
    }

    async function onSubmit() {
        if (!campaignId || !campaign || !user?.id) {
            return
        }
        const items = cartItems
        if (!items.length) {
            return
        }

        const invalid = items.filter(
            (item) =>
                !isPitchSubmittable({
                    talent: item.talent,
                    eligibility: campaign.eligibility,
                    campaignPlatforms: platforms,
                    price: item.price,
                    minPricePerCreator: campaign.minPricePerCreator,
                    maxPricePerCreator: campaign.maxPricePerCreator,
                }),
        )
        if (invalid.length) {
            toast.add({
                type: "error",
                title: "Some pitches can’t be submitted",
                description: "Check eligibility and that each price is within this opportunity’s range.",
            })
            return
        }

        setSubmitting(true)
        try {
            const result = await submitPitchesBatch({
                campaignId,
                managerId: user.id,
                managerEmail: user.email,
                items: items.map((item) => ({
                    talentId: item.talent.id,
                    talentName: creatorHandle(item.talent)?.replace(/^@/, "") || item.talent.name || item.talent.id,
                    price: item.price,
                    notes: notesToStore(item.notes),
                })),
            })

            if (result.skippedTalentIds.length) {
                setBlockedTalentIds((prev) => {
                    const next = new Set(prev)
                    for (const id of result.skippedTalentIds) {
                        next.add(id)
                    }
                    return next
                })
                setCart((prev) => omitKeys(prev, result.skippedTalentIds))
                setPriceDrafts((prev) => omitKeys(prev, result.skippedTalentIds))
                toast.add({
                    type: "error",
                    title: "Some creators were already pitched",
                    description: "Those rows were skipped. The rest were submitted.",
                })
            }

            if (!result.createdIds.length) {
                return
            }

            navigate(`/campaign/${campaignId}`)
        } catch (error) {
            console.error(error)
            analyticsService().logError(error)
            toast.add({
                type: "error",
                title: "Couldn’t submit pitches",
                description: "Refresh and try again.",
            })
        } finally {
            setSubmitting(false)
        }
    }

    return {
        loading: campaignLoading,
        allowed,
        campaign,
        campaignId,
        client,
        talentLoading,
        browse,
        cart: {
            items: cartItems,
            currency,
            submitting,
            onRemove,
            onNotesChange,
            onSubmit: () => {
                void onSubmit()
            },
        },
    }
}

function isCartablePrice(
    price: number | null | undefined,
    minPrice?: number | null,
    maxPrice?: number | null,
): price is number {
    return typeof price === "number" && price > 0 && pitchPriceWithinCampaignBounds(price, minPrice, maxPrice)
}

function notesToStore(notes?: Record<string, unknown>) {
    if (!notes || !tiptapJsonToPlainText(notes)) {
        return undefined
    }
    return notes
}

function omitKey<T extends Record<string, unknown>>(record: T, key: string): T {
    if (!(key in record)) {
        return record
    }
    const next = { ...record }
    delete next[key]
    return next
}

function omitKeys<T extends Record<string, unknown>>(record: T, keys: string[]): T {
    let next = record
    for (const key of keys) {
        next = omitKey(next, key)
    }
    return next
}
